import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { getPresenceClientKey, SITE_PRESENCE_CHANNEL } from '@/lib/site-presence'

/** Admin audit trail only; must never block Realtime or surface as an unhandled rejection. */
function logPresenceSessionEventBestEffort(
  clientKey: string,
  eventType: 'connect' | 'disconnect',
) {
  void Promise.resolve(
    supabase.rpc('log_presence_session_event', {
      p_presence_key: clientKey,
      p_event_type: eventType,
    }),
  )
    .then(({ error }) => {
      if (error && import.meta.env.DEV) {
        console.debug('[presence session log]', error.message)
      }
    })
    .catch(() => {
      // Network errors, Cloudflare 5xx (e.g. 520), or missing CORS on error pages — ignore.
    })
}

/** Count unique connected sessions: one per signed-out visitor, deduped by auth user id across tabs. */
function countFromPresenceState(
  state: Record<string, { user_id?: string | null }[]>,
): number {
  const signedIn = new Set<string>()
  let guests = 0
  for (const entries of Object.values(state)) {
    for (const row of entries) {
      const uid = row.user_id
      if (typeof uid === 'string' && uid.length > 0) signedIn.add(uid)
      else guests += 1
    }
  }
  return signedIn.size + guests
}

/**
 * Live count of users with the app open, via Supabase Realtime Presence.
 * Requires Realtime enabled on the Supabase project (default).
 * Best-effort logging of connect/disconnect for admin history (migration 057).
 */
export function useOnlinePresenceCount(authUserId: string | null) {
  const [count, setCount] = useState(0)

  useEffect(() => {
    const clientKey = getPresenceClientKey()
    const channel = supabase.channel(SITE_PRESENCE_CHANNEL, {
      config: { presence: { key: clientKey } },
    })

    const sync = () => {
      setCount(countFromPresenceState(channel.presenceState() as Record<string, { user_id?: string | null }[]>))
    }

    channel.on('presence', { event: 'sync' }, sync).subscribe(async (status) => {
      if (status !== 'SUBSCRIBED') return
      await channel.track({
        user_id: authUserId,
        online_at: new Date().toISOString(),
      })
      sync()
      logPresenceSessionEventBestEffort(clientKey, 'connect')
    })

    return () => {
      logPresenceSessionEventBestEffort(clientKey, 'disconnect')
      void supabase.removeChannel(channel)
    }
  }, [authUserId])

  return count
}
